/**
 * fc-unboxing — sticky-scroll unboxing choreography.
 *
 * Scroll position through the tall sticky scroll-zone drives the step cards / dots / progress bar
 * AND a scroll-scrubbed animation. The animation is a BAKED IMAGE SEQUENCE (frame JPGs listed by
 * the section as a JSON array): we preload the frames and draw the one matching scroll progress to
 * a <canvas>. Scrolling down plays it forward, scrolling up rewinds. There is deliberately no
 * <video> element — iOS Safari renders a paused <video> drawn to a canvas as black, which is why
 * scrubbing a video never worked on iPhone; drawing preloaded <img>s has no such limitation.
 * The canvas keeps the last drawn frame until the next one is available, so it never blanks.
 * Under prefers-reduced-motion we draw a single frame and never scrub. The card/dot/bar
 * choreography always runs on scroll.
 */
class FcUnboxing extends HTMLElement {
  connectedCallback() {
    this.scrollZone = this.querySelector('[data-fc-unbox-scroll]');
    if (!this.scrollZone) return;

    this.cards = Array.from(this.querySelectorAll('[data-fc-unbox-card]'));
    this.dots = Array.from(this.querySelectorAll('[data-fc-unbox-dot]'));
    this.bar = this.querySelector('[data-fc-unbox-bar]');
    this.canvas = this.querySelector('[data-fc-unbox-canvas]');
    this.framesEl = this.querySelector('[data-fc-unbox-frames]');
    this.total = this.cards.length;
    if (!this.total) return;

    this.current = 0;
    this.ticking = false;
    this.lastDrawn = -1;
    this.wantFrame = 0;
    this.reduceMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.onScroll = this.onScroll.bind(this);
    window.addEventListener('scroll', this.onScroll, { passive: true });

    this.setupFrames();
    this.onScroll();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
    if (this.io) this.io.disconnect();
  }

  setupFrames() {
    if (!this.canvas || !this.framesEl) return;
    let urls;
    try {
      urls = JSON.parse(this.framesEl.textContent);
    } catch (e) {
      return;
    }
    if (!Array.isArray(urls) || urls.length === 0) return;

    this.ctx = this.canvas.getContext('2d');
    this.frames = new Array(urls.length);
    this.frameCount = urls.length;

    const load = (i) => {
      const img = new Image();
      img.decoding = 'async';
      img.onload = () => {
        this.frames[i] = img;
        // Match the canvas bitmap to the real frame size once (canvas.width defaults to 300x150,
        // which would squish the square frame and tank quality). The <canvas> also carries
        // width/height attributes for the correct ratio before JS runs.
        if (!this.canvasSized && img.naturalWidth) {
          this.canvas.width = img.naturalWidth;
          this.canvas.height = img.naturalHeight;
          this.canvasSized = true;
        }
        // Paint the first frame as soon as it lands, and redraw if this frame is the one the
        // current scroll position wants (it may have arrived after we tried to draw it).
        if (i === 0 || i === this.wantFrame) this.drawFrame(this.reduceMotion ? 0 : this.wantFrame);
      };
      img.src = urls[i];
      // Pre-decode off the main thread so drawImage during scrub never pays a synchronous decode
      // (that decode-on-first-draw is what makes the scrub feel laggy). Best-effort.
      if (img.decode) img.decode().catch(() => {});
    };

    // Preload only as the section approaches the viewport (one screen ahead), so the ~2MB of
    // frames never delays initial page load.
    if ('IntersectionObserver' in window) {
      this.io = new IntersectionObserver(
        (entries, obs) => {
          if (entries.some((e) => e.isIntersecting)) {
            obs.disconnect();
            for (let i = 0; i < urls.length; i++) load(i);
          }
        },
        { rootMargin: '100% 0px' }
      );
      this.io.observe(this);
    } else {
      for (let i = 0; i < urls.length; i++) load(i);
    }
  }

  drawFrame(i) {
    const img = this.frames && this.frames[i];
    if (!this.ctx || !img || !this.canvas.width) return; // not loaded yet — keep the last frame
    this.ctx.drawImage(img, 0, 0, this.canvas.width, this.canvas.height);
    this.lastDrawn = i;
  }

  showFrameForProgress(progress) {
    if (!this.frameCount) return;
    const i = Math.min(this.frameCount - 1, Math.max(0, Math.round(progress * (this.frameCount - 1))));
    this.wantFrame = i;
    if (i !== this.lastDrawn) this.drawFrame(i);
  }

  setActive(index) {
    if (index === this.current) return;
    this.current = index;
    for (let i = 0; i < this.cards.length; i++) {
      this.cards[i].classList.toggle('is-active', i === index);
    }
    for (let i = 0; i < this.dots.length; i++) {
      this.dots[i].classList.toggle('is-active', i === index);
    }
  }

  onScroll() {
    if (this.ticking) return;
    this.ticking = true;
    requestAnimationFrame(() => {
      this.ticking = false;
      const zoneHeight = this.scrollZone.offsetHeight - window.innerHeight;
      if (zoneHeight <= 0) return;
      const scrolled = -this.scrollZone.getBoundingClientRect().top;
      const progress = Math.max(0, Math.min(1, scrolled / zoneHeight));
      const index = Math.min(Math.floor(progress * this.total), this.total - 1);
      this.setActive(index);
      if (this.bar) this.bar.style.width = `${progress * 100}%`;

      // Scrub the frame sequence. Skipped under reduced motion (frame 0 stays painted).
      if (!this.reduceMotion) this.showFrameForProgress(progress);
    });
  }
}

if (!customElements.get('fc-unboxing-component')) {
  customElements.define('fc-unboxing-component', FcUnboxing);
}
